import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cosUrl, imgFallback, shotUrl } from '../constants';
import '../styles/home.css';

/**
 * 平台六项能力 → 对应的真实产品界面截图。
 * shot 为 public/screenshots/ 下的文件名，须经 shotUrl() 引用（截图未上传 COS）。
 * 保密约束：只允许引用对外公开页面的截图；管理后台及数据大屏截图一律不得进入官网。
 * 无公开界面的能力（仓单质押/区块链存证/AI 预警）不配截图，以图标区块示意。
 */
interface Capability {
  title: string;
  desc: string;
  note: string;
  icon: React.ReactNode;
  shot?: string;
  alt?: string;
}

const CAPABILITIES: Capability[] = [
  {
    title: '牛只溯源',
    desc: '一牛一档，全生命周期追溯，扫码即可查看。',
    note: '输入耳标号即可调出该牛档案：品种、出生日期、养殖场、检疫与用药记录、历次交易与运输单据。',
    shot: 'shot-trace-detail.jpg',
    alt: '牛只溯源详情界面，展示单头牛只的档案字段与全生命周期记录',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <circle cx="8.5" cy="10.5" r="2.4" />
        <path d="M14.5 9.5h3M14.5 13h3M11 13.5c.9 1.4 2.4 1.6 3.4 0" />
      </svg>
    ),
  },
  {
    title: '牛只交易平台',
    desc: '挂牌发布、竞价撮合、成交确认在线完成。',
    note: '截图即 trade.yunniuji.com 公开大厅，无需登录即可浏览在售牛只的品种、体重、价格与产地。',
    shot: 'shot-trade-hall.jpg',
    alt: '交易市场公开大厅界面，列出在售牛只的品种、体重与报价',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 5h11M8 5l3-3M8 5l3 3" />
        <path d="M16 19H5M16 19l-3-3M16 19l-3 3" />
      </svg>
    ),
  },
  {
    title: '牛只在途监管',
    desc: 'GPS 轨迹、电子围栏、温湿度全程实时监管。',
    note: '运输途中可在 transport.yunniuji.com 查看车辆实时位置、轨迹回放，以及车厢温湿度和到站签收记录。',
    shot: 'shot-transport-intro.jpg',
    alt: '在途监管平台登录页，支持政府监管、运输企业、加工司机三种身份',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 7h11v9H3z" />
        <path d="M14 10h3.5L21 13v3h-7" />
        <circle cx="7" cy="17.5" r="1.7" />
        <circle cx="17" cy="17.5" r="1.7" />
      </svg>
    ),
  },
  {
    title: '仓单质押',
    desc: '电子仓单在线生成，动产融资登记，盘活产业资产。',
    note: '后台按头生成电子仓单，登记质押物数量、质权方与到期日，作为动产融资登记的存证依据。',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 3h9l4 4v14H6z" />
        <path d="M14 3v5h5" />
        <path d="M9 12h7M9 16h7" />
      </svg>
    ),
  },
  {
    title: '区块链存证',
    desc: '关键环节上链存证，数据不可篡改，可信可验。',
    note: '建档、交易、检疫、运输各环节写入链上哈希，官网溯源页可凭耳标回查对应的存证记录。',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3 4.5 7v5.5c0 4.4 3.2 7.6 7.5 8.5 4.3-.9 7.5-4.1 7.5-8.5V7L12 3z" />
        <path d="m9.3 11.9 1.9 1.9 3.5-3.6" />
      </svg>
    ),
  },
  {
    title: 'AI 预警',
    desc: '牛只健康与行情风险智能预警，辅助科学决策。',
    note: '基于存栏与调运数据生成价格走势与异常调运提示，预警结果在行情大屏集中呈现。',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 12h3l2.5-7 4.5 14 2.5-7h5" />
      </svg>
    ),
  },
];

// 平台已建成的系统组成（均为已交付的子系统，非经营数据统计）
const SYSTEM_PARTS = [
  { value: '7 个', label: '已上线终端' },
  { value: '2 端', label: '交易市场 · 监管平台' },
  { value: '全环节', label: '档案上链存证' },
  { value: '24 小时', label: '在途位置可见' },
];

/**
 * 平台支持的肉牛品种。
 * 品种示意图此前为 AI 生成、与实际业务无关，已改为纯文字卡片：品种名 + 特征描述 + 标签。
 */
const BREEDS = [
  {
    name: '西门塔尔',
    desc: '乳肉兼用，体型大、生长快，育肥期短、出肉率与肉质均衡，是国内肉牛杂交改良最常用的父本。',
    tag: '主力品种',
    img: 'breed-simmental.jpg',
    alt: '西门塔尔牛，黄白花被毛、白头，站在牛场栏内',
  },
  {
    name: '安格斯',
    desc: '原产英国，黑色无角基因纯合。肉牛肌间脂肪沉积良好，大理石纹细密，是雪花牛肉的常见来源。',
    tag: '高端雪花',
    img: 'breed-angus.jpg',
    alt: '安格斯牛，全身黑毛、无角，站在牛舍垫草上',
  },
  {
    name: '鲁西黄牛',
    desc: '山东本地优良地方品种，耐粗饲、抗逆性强，产肉率与肉质风味俱佳，中小规模育肥户饲养比例较高。',
    tag: '本地名种',
    img: 'breed-yellow.jpg',
    alt: '鲁西黄牛，黄褐色被毛、体格粗壮，站在农家牛圈内',
  },
];

/**
 * 首屏主视觉：山东肉牛育肥场实景（牛群采食）。
 * 图片走 COS（cosUrl），与品种卡同批生成，见 IMAGE_VERSION 版本戳。
 */
const HERO_IMG = 'website-hero.jpg';

function HeroArtwork() {
  return (
    <div className="hero-art">
      <img
        className="hero-art__img"
        src={cosUrl(HERO_IMG)}
        alt="山东肉牛育肥场实景：牛群在食槽采食"
        loading="eager"
        decoding="async"
        data-local={cosUrl(HERO_IMG)}
        onError={imgFallback}
      />
    </div>
  );
}

export default function Home() {
  useEffect(() => {
    document.title = '首页 | 云牛集·山东牛产业智慧平台';
  }, []);

  return (
    <>
      <section className="home-hero">
        <div className="container hero-inner">
          <div className="hero-copy">
            <h1>每一头牛，都有一份可核验的档案</h1>
            <p className="hero-lead">云牛集 · 山东牛产业数字化平台</p>
            <p className="hero-desc">
              从养殖场建档、交易交割到运输监管，牛只的关键环节均记录在案并上链存证。
              监管方可核查，交易方可追溯，消费者扫码即可查看。
            </p>
            <div className="hero-actions">
              <Link to="/trace" className="btn btn-primary">
                查询牛只档案
              </Link>
              <Link to="/market" className="btn btn-outline-light">
                查看参考行情
              </Link>
            </div>
          </div>
          <HeroArtwork />
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <h2>平台提供的六项功能</h2>
            <p className="section-desc">
              每一项均对应已上线的系统模块，可登录交易市场或在途监管平台直接使用
            </p>
          </div>
          <div className="cap-grid">
            {CAPABILITIES.map((item) => (
              <article key={item.title} className="cap-card">
                {item.shot ? (
                  <img
                    className="cap-media"
                    src={shotUrl(item.shot)}
                    alt={item.alt}
                    loading="lazy"
                    decoding="async"
                    data-local={shotUrl(item.shot)}
                    onError={imgFallback}
                  />
                ) : (
                  <div className="cap-media cap-media--icon" aria-hidden="true">
                    {item.icon}
                  </div>
                )}
                <div className="cap-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
                <p className="cap-note">{item.note}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section alt">
        <div className="container">
          <div className="section-head">
            <h2>平台支持的肉牛品种</h2>
            <p className="section-desc">
              建档时按品种登记血统与检疫记录，不同品种的档案字段一致，可横向比对
            </p>
          </div>
          <div className="breed-grid">
            {BREEDS.map((b) => (
              <article key={b.name} className="breed-card">
                <img
                  className="breed-media"
                  src={cosUrl(b.img)}
                  alt={b.alt}
                  loading="lazy"
                  decoding="async"
                  data-local={cosUrl(b.img)}
                  onError={imgFallback}
                />
                <div className="breed-head">
                  <h3>{b.name}</h3>
                  <span className="breed-tag">{b.tag}</span>
                </div>
                <p>{b.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="stats-band">
        <div className="container stats-inner">
          {SYSTEM_PARTS.map((item) => (
            <div key={item.label} className="stat-item">
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="section alt">
        <div className="container">
          <div className="section-head">
            <h2>平台已上线终端</h2>
            <p className="section-desc">官网、交易市场、在途监管平台均为线上可访问系统，可直接体验</p>
          </div>
          <ul className="terminal-list">
            <li>
              <strong>品牌官网</strong>
              <span>www.yunniuji.com · 产业信息与公开溯源查询</span>
            </li>
            <li>
              <strong>交易市场</strong>
              <span>trade.yunniuji.com · 挂牌、竞价、在线交割</span>
            </li>
            <li>
              <strong>在途监管平台</strong>
              <span>transport.yunniuji.com · GPS 轨迹、电子围栏、证照核验</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="home-cta">
        <div className="container cta-inner">
          <h2>先查一头牛，再谈合作</h2>
          <p>输入耳标即可查看该牛只的养殖履历与存证记录，无需注册</p>
          <div className="cta-actions">
            <Link to="/contact" className="btn btn-primary">
              免费咨询
            </Link>
            <a
              className="btn btn-teal"
              href="https://trade.yunniuji.com"
              target="_blank"
              rel="noreferrer"
            >
              进入交易市场
            </a>
            <a
              className="btn btn-teal"
              href="https://transport.yunniuji.com"
              target="_blank"
              rel="noreferrer"
            >
              进入监管平台
            </a>
            <Link to="/solutions" className="btn btn-outline-light">
              了解解决方案
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
